import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuArrowRight, LuX } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import "../css/OrderStatusPopup.css";

const POLL_INTERVAL_MS = 15000;
const MAX_STORED_IDS = 100;
const POPUP_STAGES = new Set([
  "processing",
  "preliminary_check",
  "printing",
  "quality_check",
  "shipped",
  "delivered",
  "cancelled",
]);

const STAGE_COPY = {
  processing: {
    eyebrow: "In the studio",
    title: "Your order is being prepared",
    message:
      "Every detail is now being checked and prepared with care before dispatch.",
  },
  preliminary_check: {
    eyebrow: "Preliminary check",
    title: "Your order is being checked",
    message:
      "We are reviewing the approved details and preparing everything needed for production.",
  },
  printing: {
    eyebrow: "In production",
    title: "Your order is being printed",
    message:
      "Your stationery has entered printing and is progressing through production with care.",
  },
  quality_check: {
    eyebrow: "Quality check",
    title: "Your order is being quality checked",
    message:
      "The finished stationery is being inspected carefully before it is prepared for dispatch.",
  },
  shipped: {
    eyebrow: "On the road",
    title: "Your order has shipped",
    message:
      "Your stationery is packed safely and travelling to its destination.",
  },
  delivered: {
    eyebrow: "Delivered",
    title: "Your order has arrived",
    message:
      "Your Graphe Weddings parcel has reached you. We hope every detail feels perfect.",
  },
  cancelled: {
    eyebrow: "Order update",
    title: "Your order was cancelled",
    message:
      "This order will not move forward. Open your orders for the latest details.",
  },
};

const readSeenIds = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value.map(String) : [];
  } catch {
    return [];
  }
};

const writeSeenIds = (key, ids) => {
  try {
    localStorage.setItem(key, JSON.stringify(ids.slice(0, MAX_STORED_IDS)));
  } catch {
    // The popup still remains deduplicated for the current mounted session.
  }
};

const getStage = (notification) =>
  String(notification?.metadata?.order_status || "").toLowerCase();

const ProcessingAnimation = () => (
  <div className="order-stage-scene processing-scene" aria-hidden="true">
    <span className="processing-spark spark-one" />
    <span className="processing-spark spark-two" />
    <span className="processing-spark spark-three" />
    <div className="processing-orbit orbit-one" />
    <div className="processing-orbit orbit-two" />
    <div className="processing-package">
      <span className="package-lid" />
      <span className="package-ribbon" />
      <span className="package-seal">G</span>
    </div>
    <div className="processing-belt">
      <span />
      <span />
      <span />
      <span />
    </div>
  </div>
);

const ShippingAnimation = () => (
  <div className="order-stage-scene shipping-scene" aria-hidden="true">
    <span className="shipping-cloud cloud-one" />
    <span className="shipping-cloud cloud-two" />
    <div className="shipping-truck">
      <div className="truck-cargo">
        <span className="cargo-box box-one" />
        <span className="cargo-box box-two" />
        <span className="cargo-box box-three" />
      </div>
      <div className="truck-cab">
        <span className="truck-window" />
        <span className="truck-light" />
      </div>
      <span className="truck-wheel wheel-one" />
      <span className="truck-wheel wheel-two" />
    </div>
    <div className="shipping-road"><span /></div>
  </div>
);

const DeliveredAnimation = () => (
  <div className="order-stage-scene delivered-scene" aria-hidden="true">
    <span className="delivery-confetti confetti-one" />
    <span className="delivery-confetti confetti-two" />
    <span className="delivery-confetti confetti-three" />
    <span className="delivery-confetti confetti-four" />
    <div className="delivered-package">
      <span className="delivered-lid" />
      <span className="delivered-ribbon" />
      <span className="delivered-check">✓</span>
    </div>
  </div>
);

const CancelledAnimation = () => (
  <div className="order-stage-scene cancelled-scene" aria-hidden="true">
    <div className="cancelled-ring"><span>!</span></div>
  </div>
);

const StageAnimation = ({ stage }) => {
  if (stage === "shipped") return <ShippingAnimation />;
  if (stage === "delivered") return <DeliveredAnimation />;
  if (stage === "cancelled") return <CancelledAnimation />;
  return <ProcessingAnimation />;
};

const OrderStatusPopup = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, authLoading } = useAuth();
  const [notification, setNotification] = useState(null);
  const requestInFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const userId = user?.user_id || user?.id;
  const storageKey = useMemo(
    () => (userId ? `order-stage-popups:${userId}` : null),
    [userId]
  );

  const fetchStageUpdates = useCallback(async () => {
    if (
      authLoading ||
      !isAuthenticated ||
      !storageKey ||
      requestInFlightRef.current
    ) {
      return;
    }

    requestInFlightRef.current = true;

    try {
      const response = await api.get("/customer/notifications", {
        params: { is_read: false, limit: 20 },
      });
      const notifications = Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      const seenIds = readSeenIds(storageKey);
      const seenSet = new Set(seenIds);
      const unseenStageUpdates = notifications.filter((item) => {
        const id = String(item.notification_id);
        return POPUP_STAGES.has(getStage(item)) && !seenSet.has(id);
      });

      if (!unseenStageUpdates.length) return;

      const updatedSeenIds = [
        ...unseenStageUpdates.map((item) => String(item.notification_id)),
        ...seenIds,
      ].filter((id, index, values) => values.indexOf(id) === index);

      writeSeenIds(storageKey, updatedSeenIds);

      if (mountedRef.current) {
        // The API returns newest first, so a customer sees their current stage
        // rather than a queue of outdated intermediate stages after returning.
        setNotification(unseenStageUpdates[0]);
      }
    } catch (error) {
      console.error("Order status popup fetch failed:", error);
    } finally {
      requestInFlightRef.current = false;
    }
  }, [authLoading, isAuthenticated, storageKey]);

  useEffect(() => {
    mountedRef.current = true;

    if (!isAuthenticated || authLoading) {
      setNotification(null);
      return undefined;
    }

    fetchStageUpdates();
    const interval = window.setInterval(fetchStageUpdates, POLL_INTERVAL_MS);

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") fetchStageUpdates();
    };

    window.addEventListener("focus", fetchStageUpdates);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", fetchStageUpdates);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [authLoading, fetchStageUpdates, isAuthenticated]);

  useEffect(() => {
    if (!notification) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") setNotification(null);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [notification]);

  if (!notification) return null;

  const stage = getStage(notification);
  const copy = STAGE_COPY[stage] || STAGE_COPY.processing;
  const orderNumber = notification.metadata?.order_number;

  const viewOrder = async () => {
    setNotification(null);

    try {
      await api.put(
        `/customer/notifications/${notification.notification_id}/read`
      );
      window.dispatchEvent(new Event("notifications-updated"));
    } catch (error) {
      console.error("Unable to mark order update as read:", error);
    }

    navigate("/orders");
  };

  return (
    <aside
      className={`order-stage-popup stage-${stage}`}
      role="dialog"
      aria-live="polite"
      aria-labelledby="order-stage-popup-title"
    >
      <button
        type="button"
        className="order-stage-popup-close"
        onClick={() => setNotification(null)}
        aria-label="Close order update"
      >
        <LuX />
      </button>

      <StageAnimation stage={stage} />

      <div className="order-stage-popup-copy">
        <p>{copy.eyebrow}</p>
        <h2 id="order-stage-popup-title">{copy.title}</h2>
        <span>{copy.message}</span>

        {orderNumber && (
          <small>Order {orderNumber}</small>
        )}

        <button type="button" onClick={viewOrder}>
          View order <LuArrowRight />
        </button>
      </div>
    </aside>
  );
};

export default OrderStatusPopup;
